import { BindAll } from 'lodash-decorators'
import { EnumMyStudyMenu } from '@xt/client/enums/my'
import { CollectTypeEnum, EnumVisibleRange, EnumWaterfallLoadResult, MomentSourceType } from '@xt/client/enums'
import { action, computed, observable, toJS } from 'mobx'
import { includes } from 'lodash'
import { $service } from '@xt/client/experimental/request/service'

import type { HomeworkListResponse, DairyListResponse, CollectListResponse, CommonStudyCardUIData } from './types.d'

type Option = {
  label: string
  value: string
}

type MenuConfig = {
  label: string
  value: string
  options: Array<Option>
}

type LoaderParams = {
  page: number
  pageSize: number
  lastId: number | string
  memberId: string
}

type LoaderReturn = [boolean, Array<CommonStudyCardUIData>]

type HomeworkOptionsValue = 'all' | 'preview' | 'unpreview'

const menus: Array<MenuConfig> = [
  {
    label: '作业',
    value: EnumMyStudyMenu.Homework,
    options: [
      {
        label: '全部',
        value: 'all'
      },
      {
        label: '未评阅',
        value: 'unpreview'
      },
      {
        label: '已评阅',
        value: 'preview'
      }
    ]
  },
  {
    label: '作品',
    value: EnumMyStudyMenu.Reward,
    options: []
  },
  {
    label: '日常',
    value: EnumMyStudyMenu.Daily,
    options: []
  },
  {
    label: '收藏',
    value: EnumMyStudyMenu.Collect,
    options: []
  }
]

@BindAll()
export class MyStudyController {
  readonly menus = menus
  /**
   * 当前页面active的一级分类
   */
  @observable current: EnumMyStudyMenu = EnumMyStudyMenu.Homework

  /**
   * 当前页面一级分类下的二级分类 需求目前只有作业才有二级分类
   */
  @observable option: HomeworkOptionsValue = 'all'

  @observable list: Array<CommonStudyCardUIData> = []

  isLoading: boolean = false

  page: number = 0

  readonly pageSize: number = 40

  @computed get options() {
    return this.menus.find(menu => menu.value === this.current)?.options ?? ([] as Array<Option>)
  }

  @computed get identifier() {
    return `${this.current}-${this.option}`
  }

  @action
  handleChangeCurrent(key: EnumMyStudyMenu) {
    this.current = key
    this.option = 'all'
    this.onResetPage()
  }

  @action
  handleChangeOption(opt: string) {
    // 参数需要合法
    if (includes(['all', 'preview', 'unpreview'] satisfies Array<HomeworkOptionsValue>, opt)) {
      this.option = opt as HomeworkOptionsValue
      this.onResetPage()
    }
  }

  @action
  private onAppendList(list: CommonStudyCardUIData[]) {
    this.list = [...this.list, ...list]
  }

  @action
  private onResetPage() {
    this.page = 0
    this.list = []
  }

  private transformHomeworkData(data: Array<HomeworkListResponse>): Array<CommonStudyCardUIData> {
    return data.map(item => {
      const hasImage = Array.isArray(item.picList) && item.picList.length > 0

      return {
        type: 'homework',
        reviewed: item.reviewed,
        reviewerId: item.reviewerId,
        reviewerNickname: item.reviewerNickname,
        id: item.id,
        cover: hasImage ? item.picList![0].waterUrl : undefined,
        coverWidth: hasImage ? item.picList![0].originalWidth : 235,
        coverHeight: hasImage ? item.picList![0].originalHeight : 324,
        isPublic: item.visibleRange !== EnumVisibleRange.Privacy,
        tag: item.title || '',
        title: item.homeworkTitle,
        content: item.content,
        memberId: item.memberId,
        memberNickname: item.memberNickname,
        like: false,
        likeCount: 0,
        showLike: false,
        bizType: item.bizType,
        createTime: item.createTime
      }
    })
  }

  private transformShowData(data: Array<DairyListResponse>): Array<CommonStudyCardUIData> {
    return data.map(item => {
      const hasImage = Array.isArray(item.pictures) && item.pictures.length > 0

      return {
        type: 'dairy',
        id: item.id,
        cover: hasImage ? item.pictures[0].waterUrl : undefined,
        coverWidth: hasImage ? item.pictures[0].originWidth : 235,
        coverHeight: hasImage ? item.pictures[0].originHeight : 324,
        isPublic: item.visibleRange === EnumVisibleRange.Public,
        jumpUrl: item.jumpUrl,
        tag: item.sourceDescription || '',
        title: item.title,
        content: item.content,
        memberId: item.memberId,
        memberNickname: item.memberNickname,
        like: item.like === true,
        likeCount: typeof item.likeCount === 'number' ? item.likeCount : 0,
        showLike: true,
        createTime: item.createTime
      }
    })
  }

  private transformDairyData(data: Array<DairyListResponse>): Array<CommonStudyCardUIData> {
    return data.map(item => {
      const hasImage = Array.isArray(item.pictures) && item.pictures.length > 0

      return {
        type: 'dairy',
        id: item.id,
        cover: hasImage ? item.pictures[0].waterUrl : undefined,
        coverWidth: hasImage ? item.pictures[0].originWidth : 235,
        coverHeight: hasImage ? item.pictures[0].originHeight : 324,
        isPublic: item.visibleRange === EnumVisibleRange.Public,
        tag: item.sourceDescription || '',
        title: item.title,
        content: item.content,
        memberId: item.memberId,
        memberNickname: item.memberNickname,
        like: item.like === true,
        likeCount: typeof item.likeCount === 'number' ? item.likeCount : 0,
        showLike: true,
        createTime: item.createTime
      }
    })
  }

  private transformCollectData(data: Array<CollectListResponse>): Array<CommonStudyCardUIData> {
    return data.map(item => {
      return {
        type: 'collect',
        // 唯一
        id: item.collectionId,
        cover: item.coverPictureUrl ? item.coverPictureUrl : undefined,
        coverWidth: item.originalWidth || 235,
        coverHeight: item.originalHeight || 324,
        isPublic: true,
        tag: '',
        title: item.titleOrContent,
        content: item.titleOrContent,
        memberId: item.memberId,
        memberNickname: item.memberNickname,
        like: false,
        likeCount: 0,
        showLike: false,
        createTime: item.createTime,
        collectType: item.collectType,
        jumpType: item.collectType === CollectTypeEnum.WORK ? 'show' : 'dairy',
        collectionId: item.collectionId,
        showId: item.showId,
        videoUrl: item.videoUrl
      }
    })
  }

  /**
   * 加载作业列表数据
   */
  private async loadHomeworkData({ page, pageSize, lastId, memberId }: LoaderParams): Promise<LoaderReturn> {
    const [, list] = await $service<Array<HomeworkListResponse>>({
      method: 'GET',
      url: `/homework/api/homeworks/members/{memberId}`,
      data: {
        memberId
      },
      params: {
        page,
        pageSize,
        lastId,
        memberId,
        typeKey: this.option === 'preview' ? 1 : this.option === 'unpreview' ? 0 : ''
      }
    })

    if (Array.isArray(list)) {
      const result = this.transformHomeworkData(list)

      return [false, result]
    }

    return [true, []]
  }

  /**
   * 加载作品列表数据
   */
  private async loadShowData({ page, pageSize, lastId, memberId }: LoaderParams): Promise<LoaderReturn> {
    const [, list] = await $service<Array<DairyListResponse>>({
      url: '/moment/api/moments/biz/{memberId}',
      method: 'GET',
      data: {
        memberId
      },
      params: {
        page,
        pageSize,
        lastId,
        memberId,
        sourceTypes: MomentSourceType.Show
      }
    })

    if (Array.isArray(list)) {
      const result = this.transformShowData(list)

      return [false, result]
    }

    return [true, []]
  }

  /**
   * 加载日常列表数据
   */
  private async loadDairyData({ page, pageSize, lastId, memberId }: LoaderParams): Promise<LoaderReturn> {
    const [, list] = await $service<Array<DairyListResponse>>({
      url: `/moment/api/moments/member/{memberId}`,
      method: 'GET',
      data: {
        memberId
      },
      params: {
        page,
        pageSize,
        lastId,
        memberId,
        sourceTypes: '1,2,3'
      }
    })

    if (Array.isArray(list)) {
      const result = this.transformDairyData(list)

      return [false, result]
    }

    return [true, []]
  }

  /**
   * 加载收藏列表数据
   */
  private async loadCollectData({ page, pageSize, lastId, memberId }: LoaderParams): Promise<LoaderReturn> {
    const [, list] = await $service<Array<CollectListResponse>>({
      url: `/moment/api/moments/collect/list/member/{memberId}`,
      method: 'GET',
      data: {
        memberId
      },
      params: {
        page,
        pageSize,
        lastId,
        memberId
      }
    })

    if (Array.isArray(list)) {
      const result = this.transformCollectData(list)

      return [false, result]
    }

    return [true, []]
  }

  async loadData(memberId: string): Promise<EnumWaterfallLoadResult> {
    if (this.isLoading) return EnumWaterfallLoadResult.Cancel

    this.isLoading = true
    const nextPage = this.page + 1
    const lastId = this.list.length > 0 ? this.list[this.list.length - 1].id : ''
    let result: LoaderReturn = [true, []]

    if (this.current === EnumMyStudyMenu.Homework) {
      result = await this.loadHomeworkData({ page: nextPage, pageSize: this.pageSize, lastId, memberId })
    } else if (this.current === EnumMyStudyMenu.Reward) {
      result = await this.loadShowData({ page: nextPage, pageSize: this.pageSize, lastId, memberId })
    } else if (this.current === EnumMyStudyMenu.Daily) {
      result = await this.loadDairyData({ page: nextPage, pageSize: this.pageSize, lastId, memberId })
    } else if (this.current === EnumMyStudyMenu.Collect) {
      result = await this.loadCollectData({ page: nextPage, pageSize: this.pageSize, lastId, memberId })
    }

    const hasError = result[0]
    const formatedData = result[1]

    this.isLoading = false

    if (hasError) {
      return EnumWaterfallLoadResult.Error
    }

    this.page += 1

    this.onAppendList(formatedData)

    return formatedData.length === this.pageSize ? EnumWaterfallLoadResult.Loaded : EnumWaterfallLoadResult.Complete
  }

  @action
  onChangeLikeStatus(item: CommonStudyCardUIData, action: 'like' | 'revert') {
    this.list = this.list.map(data => {
      if (data.id === item.id) {
        return {
          ...data,
          like: action === 'like' ? true : false,
          likeCount: action === 'like' ? data.likeCount + 1 : Math.max(0, data.likeCount - 1)
        }
      } else {
        return data
      }
    })
  }

  @action
  onRemoveItem(id: string | number) {
    this.list = this.list.filter(item => item.id !== id)
  }

  async onLike(item: CommonStudyCardUIData) {
    if (item.like === true) return

    this.onChangeLikeStatus(item, 'like')

    const [, success] = await $service({
      method: 'PUT',
      url: `/moment/api/moments/like/{id}`,
      data: {
        id: item.id
      }
    })

    if (success !== true) {
      this.onChangeLikeStatus(item, 'revert')
    }
  }
}
